import {
  CalendarDays,
  ChefHat,
  ClipboardList,
  FileSpreadsheet,
  LayoutGrid,
  Repeat,
  Salad,
  Star,
  Store,
  Tag,
  Truck,
  Users,
  UsersRound,
  Wallet,
  type LucideIcon,
} from "lucide-react";

import { canOpen, type Area, type BrandRole } from "@/core/brand/brand-context";

/** Every portal path in one place (links, redirects and the router all use these). */
export const ROUTES = {
  signIn: "/sign-in",
  createAccount: "/create-account",
  /** Picks the brand: straight in with one, a choice with several, invitations with none. */
  start: "/",
  invitations: "/invitations",
} as const;

/** Paths with an id in them (the router uses the `:param` patterns, links use the functions). */
export const ROUTE_PATTERNS = {
  brand: "/b/:brandId",
  /** Relative to the brand (child routes). */
  orders: "orders",
  dispatch: "dispatch",
  subscriptions: "subscriptions",
  subscription: "subscriptions/:subscriptionId",
  customers: "customers",
  customer: "customers/:userId",
  reports: "reports",
  reviews: "reviews",
  dishes: "dishes",
  plans: "plans",
  plan: "plans/:planId",
  menus: "menus",
  menuTemplates: "menus/templates",
  menuTemplate: "menus/templates/:templateId",
  promoCodes: "promo-codes",
  team: "team",
  brandSettings: "brand",
  money: "money",
  /** The address before the 2026-10 renames (bookmarks keep working). */
  oldSettings: "settings",
  payout: "money/payouts/:payoutId",
} as const;

const brandPath = (brandId: string) => `/b/${encodeURIComponent(brandId)}`;

export const routeTo = {
  brandHome: brandPath,
  /** A day's orders: what to cook, who paused, each customer's meal. */
  orders: (brandId: string) => `${brandPath(brandId)}/orders`,
  /** Sending a day's meals out: dispatched, delivered, couldn't serve. */
  dispatch: (brandId: string) => `${brandPath(brandId)}/dispatch`,
  subscriptions: (brandId: string) => `${brandPath(brandId)}/subscriptions`,
  subscription: (brandId: string, subscriptionId: string) =>
    `${brandPath(brandId)}/subscriptions/${encodeURIComponent(subscriptionId)}`,
  customers: (brandId: string) => `${brandPath(brandId)}/customers`,
  customer: (brandId: string, userId: string) =>
    `${brandPath(brandId)}/customers/${encodeURIComponent(userId)}`,
  /** The meal log; optionally for one customer. */
  reports: (brandId: string, filter: { userId?: string } = {}) =>
    `${brandPath(brandId)}/reports${filter.userId ? `?userId=${encodeURIComponent(filter.userId)}` : ""}`,
  reviews: (brandId: string) => `${brandPath(brandId)}/reviews`,
  dishes: (brandId: string) => `${brandPath(brandId)}/dishes`,
  plans: (brandId: string) => `${brandPath(brandId)}/plans`,
  plan: (brandId: string, planId: string) => `${brandPath(brandId)}/plans/${encodeURIComponent(planId)}`,
  /** A plan's menus, optionally for one plan. */
  menus: (brandId: string, planId?: string) =>
    `${brandPath(brandId)}/menus${planId ? `?plan=${encodeURIComponent(planId)}` : ""}`,
  menuTemplates: (brandId: string) => `${brandPath(brandId)}/menus/templates`,
  /** A template's editor; "new" for a new one. */
  menuTemplate: (brandId: string, templateId: string) =>
    `${brandPath(brandId)}/menus/templates/${encodeURIComponent(templateId)}`,
  promoCodes: (brandId: string) => `${brandPath(brandId)}/promo-codes`,
  team: (brandId: string) => `${brandPath(brandId)}/team`,
  /** The brand: `tab` = profile (default), slots, rules or holidays. */
  brandSettings: (brandId: string, tab?: "slots" | "rules" | "holidays") =>
    `${brandPath(brandId)}/brand${tab ? `?tab=${tab}` : ""}`,
  /** Earnings and payouts; `tab` = earnings (default), payouts or account. */
  money: (brandId: string, tab?: "payouts" | "account") =>
    `${brandPath(brandId)}/money${tab ? `?tab=${tab}` : ""}`,
  payout: (brandId: string, payoutId: string) =>
    `${brandPath(brandId)}/money/payouts/${encodeURIComponent(payoutId)}`,
};

export interface NavItem {
  label: string;
  to: string;
  icon: LucideIcon;
  /** Shown only to roles that may open it; no area = everyone in the team. */
  area?: Area;
  /** Matches only this exact path (a brand's home would otherwise match every page under it). */
  end?: boolean;
}

/** The menu for one brand, with only what this role may open. */
export function brandNav(brandId: string, role: BrandRole): { section?: string; items: NavItem[] }[] {
  const groups: { section?: string; items: NavItem[] }[] = [
    {
      items: [
        {
          label: "Overview",
          to: routeTo.brandHome(brandId),
          icon: LayoutGrid,
          end: true,
          area: "reports",
        },
        { label: "Orders", to: routeTo.orders(brandId), icon: ChefHat, area: "dispatch" },
        { label: "Dispatch", to: routeTo.dispatch(brandId), icon: Truck, area: "dispatch" },
        { label: "Subscriptions", to: routeTo.subscriptions(brandId), icon: Repeat, area: "customers" },
        { label: "Customers", to: routeTo.customers(brandId), icon: Users, area: "customers" },
        { label: "Reports", to: routeTo.reports(brandId), icon: FileSpreadsheet, area: "customers" },
        { label: "Reviews", to: routeTo.reviews(brandId), icon: Star, area: "customers" },
      ],
    },
    {
      section: "What you sell",
      items: [
        { label: "Plans", to: routeTo.plans(brandId), icon: ClipboardList, area: "catalogue" },
        { label: "Weekly menu", to: routeTo.menus(brandId), icon: CalendarDays, area: "catalogue" },
        { label: "Dishes", to: routeTo.dishes(brandId), icon: Salad, area: "catalogue" },
        { label: "Promotions", to: routeTo.promoCodes(brandId), icon: Tag, area: "catalogue" },
      ],
    },
    {
      section: "Your business",
      items: [
        { label: "Brand", to: routeTo.brandSettings(brandId), icon: Store, area: "brandSettings" },
        { label: "Team", to: routeTo.team(brandId), icon: UsersRound, area: "team" },
        { label: "Earnings and payouts", to: routeTo.money(brandId), icon: Wallet, area: "money" },
      ],
    },
  ];
  return groups
    .map((group) => ({ ...group, items: group.items.filter((i) => !i.area || canOpen(role, i.area)) }))
    .filter((group) => group.items.length > 0);
}
